'use client';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { type ComponentPropsWithoutRef, forwardRef, memo, useCallback, useLayoutEffect, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';

import { Tooltip, type TooltipProps } from '@air/primitive-tooltip';
import { cn } from '@air/utils-tailwind';

/** Keeps long labels readable without stretching into a single-line bar. */
export const TRUNCATED_TEXT_TOOLTIP_CONTENT_CLASS = 'max-w-xs whitespace-normal break-words text-left';

export type TruncatedTextWithTooltipProps = {
  text: string;
  onOverflowChange?: (isOverflowing: boolean) => void;
  tooltipProps?: Omit<TooltipProps, 'label' | 'children'>;
} & ComponentPropsWithoutRef<'span'>;

export const TruncatedTextWithTooltip = memo(
  forwardRef<HTMLSpanElement, TruncatedTextWithTooltipProps>(
    ({ text, className, onOverflowChange, tooltipProps, ...restOfProps }, forwardedRef) => {
      const [isOverflowing, setIsOverflowing] = useState(false);

      const onResize = useCallback((payload: { entry: ResizeObserverEntry | null }) => {
        if (!payload.entry?.target) {
          return;
        }

        const nextIsOverflowing = payload.entry.target.scrollWidth > payload.entry.target.clientWidth;
        setIsOverflowing((prev) => (prev === nextIsOverflowing ? prev : nextIsOverflowing));
      }, []);

      // Notify parents after paint — useResizeDetector can invoke onResize during render,
      // and calling a parent's setState there triggers React's nested-update warning.
      useLayoutEffect(() => {
        onOverflowChange?.(isOverflowing);
      }, [isOverflowing, onOverflowChange]);

      const { ref: resizeRef } = useResizeDetector({
        onResize,
        refreshMode: 'debounce',
        refreshRate: 100,
      });

      const composedRef = useComposedRefs(forwardedRef, resizeRef);

      const { className: tooltipClassName, ...restTooltipProps } = tooltipProps ?? {};

      return (
        <Tooltip
          className={cn(TRUNCATED_TEXT_TOOLTIP_CONTENT_CLASS, tooltipClassName)}
          label={isOverflowing ? text : undefined}
          {...restTooltipProps}
        >
          <span className={cn('min-w-0 truncate', className)} ref={composedRef} {...restOfProps}>
            {text}
          </span>
        </Tooltip>
      );
    },
  ),
);

TruncatedTextWithTooltip.displayName = 'TruncatedTextWithTooltip';
